/**
 * MasterComboTab.tsx
 *
 * Tab 1: "Bảng tổ hợp" (Color x Assembly combinations list).
 * Spec: docs/superpowers/plans/2026-09-22-bom-master-composer-spec.md
 */
import { Eye, Send, CheckCircle2, AlertTriangle, ExternalLink, HelpCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { formatDateFull } from '@/lib/dateFormat'
import type { MasterComboRow } from '@/services/boms/masterTypes'

interface MasterComboTabProps {
  combos: MasterComboRow[]
  loading: boolean
  onQuickView: (combo: MasterComboRow) => void
  onPublishClick: (combo: MasterComboRow) => void
  onSwitchToComposerWithPreview?: (colorCode: string, assemblyType: string) => void
}

const thClass =
  'py-2.5 px-3 border border-slate-300 bg-slate-50 font-semibold text-slate-900'
const tdClass = 'py-2.5 px-3 border border-slate-300 bg-white text-slate-900'

export function MasterComboTab({
  combos,
  loading,
  onQuickView,
  onPublishClick,
  onSwitchToComposerWithPreview,
}: MasterComboTabProps) {
  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-slate-400 border-r-transparent mb-2" />
        <p className="text-sm font-medium">Đang tải bảng tổ hợp Màu × Kết cấu...</p>
      </div>
    )
  }

  const readyCount = combos.filter((c) => c.status === 'ready').length
  const partialCount = combos.filter((c) => c.status === 'partial').length
  const emptyCount = combos.filter((c) => c.status === 'empty').length
  const publishedCount = combos.filter((c) => Boolean(c.published_bom)).length

  return (
    <div className="space-y-4">
      {/* Summary KPI Cards — keep success/warning status colors; neutralize indigo flood */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white p-3.5 rounded-lg border border-slate-300">
          <div className="text-xs text-slate-500 font-medium">Tổng số tổ hợp</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{combos.length}</div>
          <div className="text-xs text-slate-400 mt-0.5">
            {emptyCount > 0 ? `${emptyCount} tổ hợp trống` : 'Biến thể màu × Kết cấu'}
          </div>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-300">
          <div className="text-xs text-slate-600 font-medium flex items-center gap-1">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            <span>Sẵn sàng Publish</span>
          </div>
          <div className="text-xl font-bold text-slate-900 mt-1">{readyCount}</div>
          <Badge tone="success" className="mt-1">
            Đủ cả gỗ & ngũ kim
          </Badge>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-300">
          <div className="text-xs text-slate-600 font-medium flex items-center gap-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <span>Thiếu dòng</span>
          </div>
          <div className="text-xl font-bold text-slate-900 mt-1">{partialCount}</div>
          <Badge tone="warning" className="mt-1">
            Thiếu gỗ hoặc ngũ kim
          </Badge>
        </div>

        <div className="bg-white p-3.5 rounded-lg border border-slate-300">
          <div className="text-xs text-slate-600 font-medium flex items-center gap-1">
            <ExternalLink className="h-3.5 w-3.5 text-slate-500" />
            <span>Đã Publish</span>
          </div>
          <div className="text-xl font-bold text-slate-900 mt-1">{publishedCount}</div>
          <Badge tone="info" className="mt-1">
            Đang có BOM chính thức
          </Badge>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-lg border border-slate-300 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-300 flex flex-wrap items-center justify-between gap-3 bg-slate-50">
          <div>
            <h3 className="text-sm font-bold text-slate-900">
              Danh sách tổ hợp Điều kiện (Màu × Kết cấu)
            </h3>
            <p className="text-xs text-slate-500">
              Rà soát số lượng chi tiết áp dụng và bấm &quot;Xem nhanh&quot; hoặc &quot;Publish&quot; chốt phiên bản.
            </p>
          </div>
          <div className="text-xs text-slate-500 italic">
            * Không có cột SL đơn hàng (BOM mô tả định mức cho 1 SP)
          </div>
        </div>

        {combos.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <HelpCircle className="h-8 w-8 text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-medium">Sản phẩm này chưa có biến thể màu nào.</p>
            <p className="text-xs text-slate-400 mt-1">
              Vui lòng tạo biến thể màu cho sản phẩm tại trang Chi tiết sản phẩm trước.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left border-collapse text-slate-900">
              <thead>
                <tr>
                  <th className={`${thClass} w-12 text-center`}>STT</th>
                  <th className={`${thClass} w-28`}>Mã tổ hợp</th>
                  <th className={`${thClass}`}>Màu sắc</th>
                  <th className={`${thClass} w-32`}>Kết cấu</th>
                  <th className={`${thClass} w-24 text-right`}>Dòng gỗ</th>
                  <th className={`${thClass} w-24 text-right`}>Ngũ kim</th>
                  <th className={`${thClass} w-32 text-center`}>Trạng thái</th>
                  <th className={`${thClass} w-48`}>BOM đã Publish</th>
                  <th className={`${thClass} w-44 text-right`}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {combos.map((c, index) => {
                  const isReady = c.status === 'ready'
                  const isPartial = c.status === 'partial'
                  const isEmpty = c.status === 'empty'

                  return (
                    <tr
                      key={`${c.variant_id}_${c.assembly_type}`}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className={`${tdClass} text-center font-mono text-slate-500`}>
                        {index + 1}
                      </td>

                      <td className={tdClass}>
                        <button
                          type="button"
                          onClick={() =>
                            onSwitchToComposerWithPreview?.(
                              c.color_code,
                              c.assembly_type,
                            )
                          }
                          title="Bấm để chuyển sang tab Master BOM với bộ lọc xem trước này"
                          className="px-2 py-0.5 rounded font-mono font-semibold bg-slate-100 text-slate-800 border border-slate-300 hover:bg-slate-200 transition-colors"
                        >
                          {c.combo_code}
                        </button>
                      </td>

                      <td className={tdClass}>
                        <div className="font-medium text-slate-900">{c.color_name}</div>
                        <div className="text-xs font-mono text-slate-500">
                          {c.color_code}
                        </div>
                      </td>

                      <td className={tdClass}>
                        <Badge
                          tone={c.assembly_type === 'assembled' ? 'info' : 'warning'}
                        >
                          {c.assembly_label}
                        </Badge>
                      </td>

                      <td className={`${tdClass} text-right font-mono`}>
                        <span
                          className={`font-semibold ${
                            c.wood_count > 0 ? 'text-slate-900' : 'text-slate-300'
                          }`}
                        >
                          {c.wood_count}
                        </span>
                        <span className="text-slate-400 ml-1">dòng</span>
                      </td>

                      <td className={`${tdClass} text-right font-mono`}>
                        <span
                          className={`font-semibold ${
                            c.hardware_count > 0 ? 'text-slate-900' : 'text-slate-300'
                          }`}
                        >
                          {c.hardware_count}
                        </span>
                        <span className="text-slate-400 ml-1">dòng</span>
                      </td>

                      <td className={`${tdClass} text-center`}>
                        {isReady && (
                          <span data-testid="status-badge">
                            <Badge tone="success">Sẵn sàng</Badge>
                          </span>
                        )}
                        {isPartial && (
                          <span data-testid="status-badge">
                            <Badge tone="warning">Thiếu dòng</Badge>
                          </span>
                        )}
                        {isEmpty && (
                          <span data-testid="status-badge">
                            <Badge tone="neutral">Trống</Badge>
                          </span>
                        )}
                      </td>

                      <td className={tdClass}>
                        {c.published_bom ? (
                          <div className="flex flex-col">
                            <Link
                              to={`/purchasing/boms/${c.published_bom.id}`}
                              className="text-slate-800 hover:text-slate-950 font-semibold inline-flex items-center gap-1 group underline-offset-2 hover:underline"
                              title="Xem chi tiết BOM chính thức"
                            >
                              <span>
                                v{c.published_bom.version_seq} ({c.published_bom.version_label})
                              </span>
                              <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                            </Link>
                            <span className="text-xs text-slate-400">
                              Cập nhật: {formatDateFull(c.published_bom.updated_at)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-slate-400 italic text-xs">
                            Chưa publish
                          </span>
                        )}
                      </td>

                      <td className={`${tdClass} text-right`}>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="secondary"
                            onClick={() => onQuickView(c)}
                            title="Xem nhanh các dòng áp dụng cho tổ hợp này"
                            className="h-7 px-2 text-xs flex items-center gap-1"
                          >
                            <Eye className="h-3.5 w-3.5" />
                            <span>Xem</span>
                          </Button>

                          <Button
                            variant="primary"
                            onClick={() => onPublishClick(c)}
                            title="Publish chốt phiên bản BOM chính thức"
                            disabled={isEmpty}
                            className="h-7 px-2.5 text-xs flex items-center gap-1"
                          >
                            <Send className="h-3.5 w-3.5" />
                            <span>Publish</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
